import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';

const Index = () => {
  const srcTxtRef = useRef<HTMLDivElement>(null);
  const topTxtRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const tlRef = useRef<gsap.core.Timeline>();

  useEffect(() => {
    if (!srcTxtRef.current || !topTxtRef.current || !wrapperRef.current) return;

    const srcTxt = srcTxtRef.current;
    const topTxt = topTxtRef.current;
    const txtContent = srcTxt.textContent || '';
    const bb = srcTxt.getBoundingClientRect();

    // Clear any existing divs
    topTxt.innerHTML = '';

    // Create multiple div elements for the dynamic text effect
    for (let i = 0; i <= bb.width * 0.55; i++) {
      const div = document.createElement('div');
      topTxt.appendChild(div);
      gsap.set(div, {
        position: 'absolute',
        width: 4,
        height: bb.height,
        x: i * 2,
        y: -bb.height,
        textIndent: -i * 2,
        color: '#fff',
        overflow: 'hidden',
        textContent: txtContent
      });
    }

    // Set initial animation effects
    gsap.set(wrapperRef.current, {
      rotate: -50,
      skewY: 22,
      scaleX: 0.75
    });

    // Create the main timeline
    const tl = gsap.timeline({
      paused: true,
      defaults: { duration: 0.25, ease: 'power3.inOut', yoyoEase: 'sine.inOut' }
    })
    .to('.top-txt *', {
      y: '-=33',
      stagger: {
        amount: 1,
        yoyo: true,
        repeat: 1,
        ease: 'none'
      }
    });

    tlRef.current = tl;

    // Initial animation sequence
    gsap.timeline()
      .fromTo(tl, { progress: 0.9 }, { duration: 1.5, progress: 0.1, ease: 'power2.inOut' })
      .to(tl, { duration: 4, progress: 0.4, ease: 'elastic.out(0.8)' });

    // Mouse move handler
    const handlePointerMove = (e: PointerEvent) => {
      const xp = e.clientX / window.innerWidth;
      
      // Dynamic background color
      const backgroundColor = `rgb(${Math.floor(xp * 255)}, 0, ${255 - Math.floor(xp * 255)})`;
      document.body.style.background = backgroundColor;
      
      // Dynamic text color
      const textColor = `rgb(${Math.floor(xp * 255)}, ${Math.floor(xp * 100)}, ${Math.floor(xp * 200)})`;
      gsap.to('.src-txt', { color: textColor });
      
      // Dynamic shadow effect
      const shadowX = gsap.utils.mapRange(0, 1, -30, 30, xp);
      const shadowY = gsap.utils.mapRange(0, 1, -30, 30, xp);
      const shadowBlur = gsap.utils.mapRange(0, 1, 5, 25, xp);
      const shadowColor = `rgba(0, 0, 0, ${xp * 0.5})`;
      gsap.to('.src-txt', {
        textShadow: `${shadowX}px ${shadowY}px ${shadowBlur}px ${shadowColor}`,
      });
      
      // Update animation progress
      gsap.to(tl, { progress: xp, overwrite: true });
      gsap.to(wrapperRef.current, {
        x: gsap.utils.mapRange(0, 1, 30, -30, xp),
        y: gsap.utils.mapRange(0, 1, -30, 30, xp)
      });
    };

    // Mouse down handler
    const handleMouseDown = () => {
      gsap.timeline({ defaults: { duration: 0.2, overwrite: 'auto' } })
        .to('.top-txt', { y: -25 })
        .to('.src-txt', {
          filter: 'blur(2px)',
          opacity: 0.85,
          scale: 0.96,
          transformOrigin: '50% 50%'
        }, 0);
    };

    // Mouse up handler
    const handleMouseUp = () => {
      gsap.timeline({ defaults: { ease: 'bounce' } })
        .to('.top-txt', { y: 0 })
        .to('.src-txt', {
          filter: 'blur(0px)',
          opacity: 1,
          scale: 1
        }, 0);
    };

    // Add event listeners
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mouseup', handleMouseUp);

    // Cleanup
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mouseup', handleMouseUp);
      if (tlRef.current) {
        tlRef.current.kill();
      }
    };
  }, []);

  return (
    <div className="wrapper" ref={wrapperRef}>
      <div className="src-txt" ref={srcTxtRef}>
        AIPPY -- LET YOUR IDEAS FLOW
      </div>
      <div className="top-txt" ref={topTxtRef} aria-disabled="true"></div>
    </div>
  );
};

export default Index;